/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { forwardRef, useEffect, useRef, type Ref } from "react";
import "./FileManagerLeftSideTree.css";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import { Formatters, type SlickGrid as SlickGridType } from "slickgrid-react";
import useFileManagerLeftSideTree from "./useFileManagerLeftSideTree";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import { renderToStaticMarkup } from "react-dom/server";
import { t } from "@i18n";
import type { ColumnDefinition, GridClickEvent, GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { FileManagerData, FileManagerRow } from "./fileSystemServer.schema";

/** One node of the folder tree: a file-manager row plus the grid's own row markers. */
type FolderTreeRow = GridRowBase & { name?: string };

/**
 * Renders the Directory column: the folder expand/collapse toggle plus the folder name.
 *
 * `Formatters.tree` cannot be handed to `ColumnDefinition["formatter"]` directly — that signature
 * declares `columnDef: ColumnDefinition<T, K>`, and `ColumnDefinition` is not assignable to
 * slickgrid's own `Column` (its `ExcelExportColumnOptions.valueParserCallback` returns `unknown`
 * where the library's `GetDataValueCallback` returns `string | number | Date | ExcelColumnMetadata`).
 * The column the library formatter wants is therefore read back off the grid at the library's own
 * type, exactly as `GridColumnChooser.treeCellFormatter` does: `getColumns()[cell]` is the very
 * object slickgrid would have passed, since `appendCellHtml` calls the formatter with
 * `this.columns[cell]` and `getColumns()` returns `this.columns`.
 */
function treeCellFormatter(
  row: number,
  cell: number,
  value: string | undefined,
  _columnDef: ColumnDefinition<FolderTreeRow, "name">,
  dataContext: FolderTreeRow,
  grid?: SlickGridType,
): unknown {
  const column = grid?.getColumns()[cell];
  if (!grid || !column) return "";
  return Formatters.tree(row, cell, value, column, dataContext, grid);
}

/** Method shorthand on the callbacks — see the note in `useFileManagerShared`. */
interface LeftSideTreeProps {
  setSelectedFolder(folder: FileManagerRow): void;
  selectedFolder: FileManagerRow;
  fileManagerData: FileManagerData;
  onFileLoad(files: FileManagerRow[]): void;
  totalFolderCount?: number;
}

const LeftSideTree = forwardRef(
  (
    { setSelectedFolder, selectedFolder, fileManagerData, onFileLoad, totalFolderCount }: LeftSideTreeProps,
    ref: Ref<SlickGrid1Ref<FolderTreeRow>>,
  ) => {
    const tempRef = useRef<SlickGrid1Ref<FolderTreeRow>>(null);
    // Only an object ref can be read back from; a parent that hands down a callback ref (or none)
    // gets the internal one, because `syncSelectedFolder`/`collapseParentNodesWithoutChildData`
    // below need a `.current` to read the grid off.
    const treeViewRef = typeof ref === "object" && ref !== null ? ref : tempRef;

    const {
      // State
      folderTreeData,

      // Handlers
      handleOnClick,
      titleFormatter,
      collapseParentNodesWithoutChildData,
      syncSelectedFolder,
    } = useFileManagerLeftSideTree({
      setSelectedFolder,
      selectedFolder,
      fileManagerData,
      onFileLoad,
    });

    // Sync selected folder
    useEffect(() => {
      syncSelectedFolder(treeViewRef);
    }, [selectedFolder.id, folderTreeData]);

    const getTreeView = () => (
      <SlickGrid1
        ref={treeViewRef}
        gridOptions={{
          enableCheckboxSelector: false,
          enableContextMenu: false,
          forceFitColumns: true,
        }}
        titleFormatter={titleFormatter}
        selectCheckBoxOnRowClick={true}
        disableTreeCollapseOnDataChange={true}
        enableMultiSelect={false}
        surpassedDataset={folderTreeData}
        enablePointerForRows
        onClick={(args: GridClickEvent) => handleOnClick(args, treeViewRef)}
        disableRowBorder={true}
        disableColumnBorder={true}
        disableOuterBorder={true}
        hasChildMapping="hasChild"
        parentIdMapping="parentId"
        treeGrid={true}
        rowUnitLabel={t("folders")}
        totalRowCount={totalFolderCount}
        setGridReady={() => collapseParentNodesWithoutChildData(treeViewRef)}
        disableChildDataFetch
        initialSortColumnId="name"
        columnDefinitions={typedColumns<FolderTreeRow>([
          {
            id: "name",
            name: "Directory",
            field: "name",
            sortable: true,
            resizable: true,
            filterType: "Text",
            headerRenderer: () => renderToStaticMarkup(<HeaderWithIcon icon="directory" label={t("Directory")} />),
            formatter: treeCellFormatter,
            minWidth: 300,
          },
        ])}
        gridHeight="100%"
      />
    );

    return <div className="w-full overflow-auto flex h-full">{getTreeView()}</div>;
  },
);

LeftSideTree.displayName = "LeftSideTree";
export default LeftSideTree;
